import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOKEN_KEY = "fc_token";
const USER_KEY = "fc_user";

export const getToken = () => localStorage.getItem(TOKEN_KEY) || "";
export const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY));
  } catch {
    return null;
  }
};
const storeSession = (token, user) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};
// Saves locally and asks the server to back the login up in a 1-year HttpOnly cookie.
const saveSession = (token, user) => {
  storeSession(token, user);
  axios.post(`${API}/auth/session`, {}, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }).catch(() => {});
};
const restoreSession = async (headers) => {
  const { data } = await axios.post(`${API}/auth/session`, {}, { headers, withCredentials: true });
  storeSession(data.token, data.user);
  return data.user;
};
export const clearSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

// A guest's free-games session lasts until they go back to Home.
export const freeGame = {
  active: () => sessionStorage.getItem("fc_free_active") === "1",
  start: () => sessionStorage.setItem("fc_free_active", "1"),
  end: () => sessionStorage.removeItem("fc_free_active"),
};

axios.interceptors.response.use(undefined, (err) => {
  if (err?.response?.status === 403 && /free games? (is|are) used up/i.test(err.response.data?.detail || "") && freeGame.active()) {
    freeGame.end();
    window.location.reload();
  }
  return Promise.reject(err);
});

export const shareLink = (kind, id) => `${window.location.origin}/api/share/${kind}/${id}`;

const celebrateUnlock = (coins) => {
  localStorage.setItem("fc_unlocked", String(coins ?? 0));
  window.dispatchEvent(new Event("fc-unlocked"));
};

const authHeader = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const auth = {
  register: async ({ email, password, username }) => {
    const ref = localStorage.getItem("fc_ref") || undefined;
    const { data } = await axios.post(`${API}/auth/register`, { email, password, username, ref });
    saveSession(data.token, data.user);
    localStorage.removeItem("fc_ref");
    return data.user;
  },
  login: async ({ email, password }) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    saveSession(data.token, data.user);
    return data.user;
  },
  guest: async (username) => {
    const { data } = await axios.post(`${API}/auth/guest`, { username });
    saveSession(data.token, data.user);
    return data.user;
  },
  google: async (sessionId) => {
    const ref = localStorage.getItem("fc_ref") || undefined;
    const wasGuest = !!getStoredUser()?.is_guest;
    const { data } = await axios.post(`${API}/auth/google`, { session_id: sessionId, ref });
    saveSession(data.token, data.user);
    if (wasGuest && data.is_new) { celebrateUnlock(data.user.coins); data.unlocked = true; }
    localStorage.removeItem("fc_ref");
    return data;
  },
  upgrade: async ({ email, password, username }) => {
    const { data } = await axios.post(`${API}/auth/upgrade`, { email, password, username }, { headers: authHeader() });
    saveSession(data.token, data.user);
    celebrateUnlock(data.user.coins);
    return data.user;
  },
  // Returns the saved account (refreshing the 1-year session, or restoring it from the backup cookie), else a new guest.
  // With { create: false } it returns null instead of creating a guest (so page views alone don't make accounts).
  ensure: async ({ create = true } = {}) => {
    const dead = (e) => e?.response?.status === 401 || e?.response?.status === 404;
    try {
      return await restoreSession(authHeader());
    } catch (e) {
      if (!dead(e)) {
        // Network/CORS hiccup: keep the saved account rather than looking signed out.
        const stored = getStoredUser();
        if (!e?.response && getToken() && stored) return stored;
        throw e;
      }
    }
    if (getToken()) {
      clearSession();
      try { return await restoreSession({}); } catch (e) { if (!dead(e)) throw e; }
    }
    return create ? auth.guest() : null;
  },
  me: async () => {
    const { data } = await axios.get(`${API}/auth/me`, { headers: authHeader() });
    localStorage.setItem(USER_KEY, JSON.stringify(data));
    return data;
  },
  logout: () => {
    clearSession();
    return axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
  },
};

export const api = {
  createMatch: async (stake, target, friend) =>
    (await axios.post(`${API}/matches`, { stake, target, friend }, { headers: authHeader() })).data,
  incomingMatches: async () =>
    (await axios.get(`${API}/matches/incoming`, { headers: authHeader() })).data,
  getMatch: async (id) => (await axios.get(`${API}/matches/${id}`, { headers: authHeader() })).data,
  joinMatch: async (id) =>
    (await axios.post(`${API}/matches/${id}/join`, {}, { headers: authHeader() })).data,
  readyMatch: async (id) =>
    (await axios.post(`${API}/matches/${id}/ready`, {}, { headers: authHeader() })).data,
  pushBoard: async (id, board) =>
    (await axios.post(`${API}/matches/${id}/board`, { board }, { headers: authHeader() })).data,
  lockMatch: async (id, summary) =>
    (await axios.post(`${API}/matches/${id}/lock`, { summary }, { headers: authHeader() })).data,
  recentWinners: async () => (await axios.get(`${API}/winners/recent`)).data.winners,
  topEarners: async () => (await axios.get(`${API}/earners/top`)).data.earners,
  rematchMatch: async (id) =>
    (await axios.post(`${API}/matches/${id}/rematch`, {}, { headers: authHeader() })).data,
  cancelMatch: async (id) =>
    (await axios.post(`${API}/matches/${id}/cancel`, {}, { headers: authHeader() })).data,
  buyCoins: async (amount) =>
    (await axios.post(`${API}/wallet/buy`, { amount }, { headers: authHeader() })).data,
  withdrawCoins: async (amount) =>
    (await axios.post(`${API}/wallet/withdraw`, { amount }, { headers: authHeader() })).data,
  prizesMe: async () => (await axios.get(`${API}/prizes/me`, { headers: authHeader() })).data,
  prizesLeaderboard: async () => (await axios.get(`${API}/prizes/leaderboard`)).data,
  claimPrize: async (payload) =>
    (await axios.post(`${API}/prizes/claim`, payload, { headers: authHeader() })).data,
  adminClaims: async () => (await axios.get(`${API}/admin/claims`, { headers: authHeader() })).data,
  adminSetClaimStatus: async (id, status, notes) =>
    (await axios.post(`${API}/admin/claims/${id}/status`, { status, notes }, { headers: authHeader() })).data,
  pepPlay: async (stake = 0) => (await axios.post(`${API}/pep/play`, { stake }, { headers: authHeader() })).data,
  pepResult: async (won, myPoints = 0, theirPoints = 0, draw = false) =>
    (await axios.post(`${API}/pep/result`, { won, draw, my_points: myPoints, their_points: theirPoints }, { headers: authHeader() })).data,
  history: async () => (await axios.get(`${API}/history`, { headers: authHeader() })).data,
  usernameAvailable: async (u) => (await axios.get(`${API}/auth/username_available`, { params: { u } })).data,
  emailAvailable: async (e) => (await axios.get(`${API}/auth/email_available`, { params: { e } })).data,
  pepSpin: async () => (await axios.post(`${API}/pep/spin`, {}, { headers: authHeader() })).data,
  pepSpins: async () => (await axios.get(`${API}/pep/spins`, { headers: authHeader() })).data,
  listWheels: async () => (await axios.get(`${API}/wheels`, { headers: authHeader() })).data,
  wheelSpin: async (tier) => (await axios.post(`${API}/wheels/${tier}/spin`, {}, { headers: authHeader() })).data,
  claimSpin: async (id, payload) =>
    (await axios.post(`${API}/pep/spins/${id}/claim`, payload, { headers: authHeader() })).data,
};

export const dailyApi = {
  get: async () => (await axios.get(`${API}/daily`, { headers: authHeader() })).data,
  checkin: async () => (await axios.post(`${API}/daily/checkin`, {}, { headers: authHeader() })).data,
  result: async (payload) => (await axios.post(`${API}/daily/result`, payload, { headers: authHeader() })).data,
  clashes: async () => (await axios.get(`${API}/clashes`, { headers: authHeader() })).data,
  notifications: async () => (await axios.get(`${API}/notifications`, { headers: authHeader() })).data,
  readNotifications: async () => (await axios.post(`${API}/notifications/read`, {}, { headers: authHeader() })).data,
};

export const friendsApi = {
  list: async () => (await axios.get(`${API}/friends`, { headers: authHeader() })).data,
  leaderboard: async () => (await axios.get(`${API}/friends/leaderboard`, { headers: authHeader() })).data.leaderboard,
  search: async (q) => (await axios.get(`${API}/users/search`, { params: { q }, headers: authHeader() })).data.users,
  add: async (username) => (await axios.post(`${API}/friends`, { username }, { headers: authHeader() })).data.friend,
  decline: async (username) => (await axios.post(`${API}/friends/${encodeURIComponent(username)}/decline`, {}, { headers: authHeader() })).data,
  remove: async (username) => (await axios.delete(`${API}/friends/${encodeURIComponent(username)}`, { headers: authHeader() })).data,
};

const so = (path) => `${API}/shootouts${path}`;
export const shootoutApi = {
  create: async (stake, friend) => (await axios.post(so(""), { stake, friend }, { headers: authHeader() })).data,
  get: async (id) => (await axios.get(so(`/${id}`), { headers: authHeader() })).data,
  pep: async (stake = 0) => (await axios.post(so("/pep"), { stake }, { headers: authHeader() })).data,
  join: async (id) => (await axios.post(so(`/${id}/join`), {}, { headers: authHeader() })).data,
  cancel: async (id) => (await axios.post(so(`/${id}/cancel`), {}, { headers: authHeader() })).data,
  rematch: async (id) => (await axios.post(so(`/${id}/rematch`), {}, { headers: authHeader() })).data,
  squad: async (id, taker, keeper) => (await axios.post(so(`/${id}/squad`), { taker, keeper }, { headers: authHeader() })).data,
  kick: async (id, round, shot, dive) => (await axios.post(so(`/${id}/kick`), { round, shot, dive }, { headers: authHeader() })).data,
};

export const summaryFrom = (result) => ({
  points: result.points,
  w: result.w,
  d: result.d,
  l: result.l,
  gd: result.gd,
  avg: result.avg,
  position: result.position,
  verdict: result.verdict,
  perfect: result.perfect,
  fixtures: result.fixtures,
});
